import React from "react";

import {
    FRAG_BLUR,
    FRAG_FLAT,
    GLCanvas,
    type GLProgram,
    type GLSize,
    GL_BG,
    GL_RENDER,
    type PoleBuffers,
    type RenderTarget,
    bindRenderTarget,
    blurChain,
    createFullscreenVAO,
    createPoleBuffers,
    createProgram,
    createRenderTarget,
    deleteRenderTarget,
    drawFullscreen,
    gradientSource,
    hexToRgb,
    simulatePoles,
    sizePassTargets,
} from "../../shared/gl";
import {
    FEATUREDWORK_MOTION,
    FEATUREDWORK_POLES,
    FEATUREDWORK as W,
} from "./config";
import { lag } from "./lag";

const TAU = Math.PI * 2;
const BG = hexToRgb(GL_BG);

/** The pointer's field, in the section's p-space */
interface Field {
    x: number;
    y: number;
    tx: number;
    ty: number;
    amt: number;
    want: number;
    seen: boolean;
}

interface Scene {
    grad: GLProgram;
    blur: GLProgram;
    flat: GLProgram;
    vao: WebGLVertexArrayObject;
    targets: { grad: RenderTarget; ping: RenderTarget; pong: RenderTarget };
    poles: PoleBuffers;
    size: GLSize | null;
}

const onInit = (gl: WebGL2RenderingContext): Scene => ({
    grad: createProgram(gl, gradientSource(FEATUREDWORK_POLES.length)),
    blur: createProgram(gl, FRAG_BLUR),
    flat: createProgram(gl, FRAG_FLAT),
    vao: createFullscreenVAO(gl),
    targets: {
        grad: createRenderTarget(gl),
        ping: createRenderTarget(gl),
        pong: createRenderTarget(gl),
    },
    poles: createPoleBuffers(FEATUREDWORK_POLES.length),
    size: null,
});

const onResize = (gl: WebGL2RenderingContext, size: GLSize, s: Scene) => {
    s.size = size;
    sizePassTargets(
        gl,
        s.targets,
        size.width,
        size.height,
        GL_RENDER.gradScale,
        GL_RENDER.blurScale,
    );
};

const onDispose = (gl: WebGL2RenderingContext, s: Scene) => {
    gl.deleteProgram(s.grad.program);
    gl.deleteProgram(s.blur.program);
    gl.deleteProgram(s.flat.program);
    gl.deleteVertexArray(s.vao);
    deleteRenderTarget(gl, s.targets.grad);
    deleteRenderTarget(gl, s.targets.ping);
    deleteRenderTarget(gl, s.targets.pong);
};

const draw = (gl: WebGL2RenderingContext, s: Scene, t: number, fld: Field) => {
    if (!s.size) return;
    const { width, height, cssWidth, cssHeight, dpr } = s.size;
    const aspect = cssWidth / cssHeight;
    simulatePoles(t, FEATUREDWORK_POLES, FEATUREDWORK_MOTION, BG, s.poles, {
        roundRadiusGain: 0.5,
    });

    bindRenderTarget(gl, s.targets.grad);
    gl.useProgram(s.grad.program);
    const g = s.grad.uniforms;
    gl.uniform2f(g.uRes, s.targets.grad.w, s.targets.grad.h);
    gl.uniform1f(g.uTime, t);
    gl.uniform1f(g.uAspect, aspect);
    gl.uniform2fv(g.uShapePos, s.poles.shapePos);
    gl.uniform2fv(g.uColorPos, s.poles.colorPos);
    gl.uniform1fv(g.uPoleRad, s.poles.poleRad);
    gl.uniform3fv(g.uPoleCol, s.poles.poleCol);
    gl.uniform1f(g.uScale, W.scale);
    gl.uniform1f(g.uOffX, W.offsetX);
    gl.uniform1f(g.uOffY, W.offsetY);
    gl.uniform1f(g.uShapeWarpAmp, W.shapeWarpAmp);
    gl.uniform1f(g.uShapeWarpFreq, W.shapeWarpFreq);
    gl.uniform1f(g.uShapeWarpSpeed, W.shapeWarpSpeed);
    gl.uniform1f(g.uColorWarpAmp, W.colorWarpAmp);
    gl.uniform1f(g.uColorWarpFreq, W.colorWarpFreq);
    gl.uniform1f(g.uColorWarpSpeed, W.colorWarpSpeed);
    gl.uniform1f(g.uEdgeGain, W.edgeGain);
    gl.uniform1f(g.uEdgeShape, W.edgeShape);
    gl.uniform1f(g.uFalloff, W.falloff);
    gl.uniform1f(g.uColorFalloff, W.colorFalloff);
    gl.uniform2f(g.uCurPos, fld.x, fld.y);
    gl.uniform1f(g.uCurAmt, fld.amt);
    gl.uniform1f(g.uCurRadius, W.curRadius);
    gl.uniform1f(g.uCurWarp, W.curWarp);
    gl.uniform1f(g.uCurSat, W.curSat);
    gl.uniform1f(g.uCurHue, W.curHue);
    gl.uniform1f(g.uFloorAt, W.floorAt);
    gl.uniform1f(g.uFloorSoft, W.floorSoft);
    gl.uniform1f(g.uCurSpread, W.curSpread);
    drawFullscreen(gl);

    // the softness itself breathes
    const soft = 1 + W.blurBreath * Math.sin((TAU * t) / W.blurBreathPeriod);
    const blurred = blurChain(
        gl,
        s.blur,
        s.targets.grad,
        s.targets.ping,
        s.targets.pong,
        W.blurPx * soft * dpr * GL_RENDER.blurScale,
        W.blurIterations,
    );

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, width, height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(s.flat.program);
    const f = s.flat.uniforms;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, blurred.tex);
    gl.uniform1i(f.uTex, 0);
    gl.uniform2f(f.uRes, width, height);
    gl.uniform2f(f.uCssRes, cssWidth, cssHeight);
    gl.uniform3fv(f.uBg, BG);
    gl.uniform1f(f.uTime, t);
    gl.uniform1f(f.uDither, GL_RENDER.dither);
    gl.uniform1f(f.uAspect, aspect);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, s.targets.grad.tex);
    gl.uniform1i(f.uSharp, 1);
    gl.uniform2f(f.uCurPos, fld.x, fld.y);
    gl.uniform1f(f.uCurAmt, fld.amt);
    gl.uniform1f(f.uCurRadius, W.curRadius);
    gl.uniform1f(f.uCurBlur, W.curBlur);
    gl.activeTexture(gl.TEXTURE0);
    drawFullscreen(gl);
};

interface Props {
    className: string;
    /** The section, whose box the pointer is mapped into */
    sectionRef: React.RefObject<HTMLElement | null>;
}

/**
 * Featured Work's cool gradient (featuredwork.js): a blue mass anchored low in
 * the section, alive inside, leaning toward the pointer while it is over the
 * section. Drawn behind the content; the canvas runs past the section's bottom
 * so the Expertise dome's arc, not the canvas edge, ends it.
 */
export const Gradient = (props: Props) => {
    const field = React.useRef<Field>({
        x: 0,
        y: 0,
        tx: 0,
        ty: 0,
        amt: 0,
        want: 0,
        seen: false,
    });
    const start = React.useRef<number | null>(null);

    React.useEffect(() => {
        const fld = field.current;
        const onMove = (ev: PointerEvent) => {
            const section = props.sectionRef.current;
            if (!section) return;
            const r = section.getBoundingClientRect();
            const w = r.width || 1;
            const h = r.height || 1;
            fld.tx = ((ev.clientX - r.left) / w - 0.5) * (w / h);
            fld.ty = (ev.clientY - r.top) / h - 0.5;
            if (!fld.seen) {
                fld.x = fld.tx;
                fld.y = fld.ty;
                fld.seen = true;
            }
            // only on while the pointer is over the section
            fld.want = ev.clientY > r.top && ev.clientY < r.bottom ? 1 : 0;
        };
        const off = () => {
            fld.want = 0;
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        document.documentElement.addEventListener("pointerleave", off);
        window.addEventListener("blur", off);
        return () => {
            window.removeEventListener("pointermove", onMove);
            document.documentElement.removeEventListener("pointerleave", off);
            window.removeEventListener("blur", off);
        };
    }, [props.sectionRef]);

    const onFrame = (
        gl: WebGL2RenderingContext,
        now: number,
        dt: number,
        s: Scene,
    ) => {
        // Scene time follows the clock, so it keeps running while the canvas
        // is off screen and not drawn, as in the prototype
        start.current ??= now;
        const t = (now - start.current) / 1000;
        const fld = field.current;
        const ease = lag(W.curEase, dt);
        fld.x += (fld.tx - fld.x) * ease;
        fld.y += (fld.ty - fld.y) * ease;
        fld.amt += (fld.want - fld.amt) * lag(W.curFade, dt);
        draw(gl, s, t, fld);
    };

    return (
        <GLCanvas
            className={props.className}
            dprMax={W.dprMax}
            onInit={onInit}
            onResize={onResize}
            onFrame={onFrame}
            onDispose={onDispose}
        />
    );
};
